import { Shield, Users, Palette, BookOpen, HeartHandshake, GraduationCap } from 'lucide-react';

export default function About() {
  const features = [
    {
      icon: Shield,
      title: 'Safe and Caring Environment',
      description: 'A warm preschool environment where children feel secure and supported.'
    },
    {
      icon: Users,
      title: 'Social and Emotional Development',
      description: 'Children build confidence, communication and positive classroom habits.'
    },
    {
      icon: BookOpen,
      title: 'Activity-Based Learning',
      description: 'Hands-on classroom experiences connect learning with play and exploration.'
    },
    {
      icon: Palette,
      title: 'Creative Activities',
      description: 'Music, movement, art and sensory experiences encourage curiosity.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-white dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            About Tiny Wings Preschool
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Tiny Wings Preschool is a child-focused preschool in Chhatrapati Sambhaji Nagar, Maharashtra, offering Playgroup, Nursery, Junior KG and Senior KG programs for young children.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 hover:shadow-xl transition transform hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-pink-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.pexels.com/photos/8613261/pexels-photo-8613261.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Children participating in activity-based learning at Tiny Wings Preschool"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>

          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
              Our Learning Approach
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              Tiny Wings Preschool is a child-focused preschool in Chhatrapati Sambhaji Nagar, Maharashtra, offering Playgroup, Nursery, Junior KG and Senior KG programs for young children.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              Our preschool provides a safe, caring and stimulating environment where children learn through activities, play, exploration and meaningful classroom experiences. We focus on early childhood development, communication skills, confidence, creativity, social development and school readiness.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              At Tiny Wings Preschool, our learning approach includes activity-based learning, phonics, language development, early mathematics, environmental awareness, creative activities, music, movement and hands-on experiences.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              Located near Mayur Park and Datta Mandir, Tiny Wings Preschool aims to provide families in and around the area with a warm and supportive early learning environment.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-pink-100 dark:bg-pink-900/30 px-6 py-3 rounded-full">
                <span className="text-pink-700 dark:text-pink-300 font-semibold">Why Choose Tiny Wings Preschool?</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Why Choose Tiny Wings Preschool?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Activity-based learning',
              'Phonics and early language development',
              'Safe and caring environment',
              'Age-appropriate learning experiences',
              'Creative and sensory activities',
              'Social and emotional development',
              'School-readiness preparation',
              'Playgroup, Nursery, Jr KG & Sr KG',
              'Regular parent communication'
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <HeartHandshake className="w-5 h-5 text-pink-500" />
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
